import { parseISO } from 'date-fns'
import { FC } from 'react'
import { EventReportItem } from '../../../../components/EventReportItem'
import { ReportsTabWrapper } from '../ReportsTabWrapper/ReportsTabWrapper'

import { TEventRegisteredUsers } from '../../../../../../../../models/event-registered-users/event-registered-users.interface'
import { IEventInterface } from '../../../../../../../../models/events/event.interface'
import { IRegistrationByDayInterface } from '../../../../../../../../models/registrations-by-day/registrationByDay.interface'
import { ITeamMember } from '../../../../../../../../models/users/user.interface'
import { IReports } from '../../ManageClinicReportsRoot'

import { createEntriesMasterListPdf } from '../../../../../../../../templates/pdf/reports/event-reports/entries-master-list'
import { createOrderOfGoByClassPdf } from '../../../../../../../../templates/pdf/reports/event-reports/entries-order-of-go-by-class'

import { createOrderOfGoByRiderPdf } from '../../../../../../../../templates/pdf/reports/event-reports/entries-order-of-go-by-rider'

import { MESSAGES_CONST } from '../../../../../../../../const/messages-const'

import helpers from '../../../../../../../../commonHelpers/helpers'
import {
  convertDateString,
  getDayOfWeek,
  groupByKey,
  sortByKey,
} from '../../../../../../../../helpers/pdf'
import { getRegisteredRiders } from '../../../../../../../../helpers/riders'
import {
  convertTime,
  extractDatePart,
  isValidIncrementFormat,
} from '../../../../../../../../helpers/time'
import useToasterHelper from '../../../../../../../../helpers/ToasterHelper'
import { RegistrationFeesType } from '../../../../../../../../models/event-fees/event-fees.interface'
import { IEventScheduleInterface } from '../../../../../../../../models/event-scheduling/event-scheduling.interface'
import { HORSE_NAME_DEFAULT } from '../../../../../../../../models/registrations-by-day/registrationByDay.interface'
import { createClassScheduleByDayAndFacility } from '../../../../../../../../templates/pdf/reports/event-reports/entries-class-schedule-by-day-facility'
import {
  createOrderOfGoByDayAndRing,
  GroupedRows,
} from '../../../../../../../../templates/pdf/reports/event-reports/entries-order-of-go-by-day-ring'
import { PAYMENT_STATUS, PAYMENT_TYPE } from '../../../../../../../../types/payment'

interface EventReportsTabProps {
  event: IEventInterface | null
  eventId: string
  registeredUsersByDay: IRegistrationByDayInterface[] | null
  uniqueRegisteredUsersByDay: IRegistrationByDayInterface[] | null
  registrationFees: any
  eventRegisteredUsers: TEventRegisteredUsers[] | null
  teamMembers: ITeamMember[] | null
  activeOption: IReports
  scheduleBy: 'class' | 'rider'
  customScheduling?: IEventScheduleInterface['custom'] | null
  setActiveOption: (value: IReports) => void
}
const EventReportsReport: FC<EventReportsTabProps> = ({
  event,
  registeredUsersByDay,
  uniqueRegisteredUsersByDay,
  registrationFees,
  teamMembers,
  scheduleBy,
  eventId,
  activeOption,
  customScheduling,
  setActiveOption,
}) => {
  const toastFunctions = useToasterHelper()

  const onDownloadEntriesPdf = async () => {
    const rows: string[][] = []
    const trainers: string[] = []
    const horses: string[] = []
    const activeClasses: string[] = []

    try {
      // create rows
      uniqueRegisteredUsersByDay?.forEach((registeredUserByDay) => {
        const coach = teamMembers?.find(
          (member) =>
            member.horseId === registeredUserByDay.horseId && member.memberRole === 'Coach'
        )

        const coachName = coach?.memberName ?? 'No coach'

        const teamMember = teamMembers?.find(
          (member) =>
            member.horseId === registeredUserByDay.horseId && member.memberRole === 'Trainer'
        )

        const userRegistrations = registeredUsersByDay?.filter(
          (registered) =>
            registered.riderId === registeredUserByDay.riderId &&
            registered.horseId === registeredUserByDay.horseId
        )

        const classes = userRegistrations?.map((registration) => {
          const className = registration.registrationByDayName

          return registration.isScratched ? `--${className}--` : className
        })

        const row = [
          `**${registeredUserByDay.backNumber}**`,
          `${registeredUserByDay.riderName} | ^^${coachName}^^`,
          `${registeredUserByDay.horseName ?? HORSE_NAME_DEFAULT} | ^^${teamMember?.memberName ?? 'No trainer'}^^`,
          `${classes?.join(' | ')}`,
          `${classes?.length}`,
        ]

        rows.push(row)

        if (teamMember?.memberName) trainers.push(teamMember.memberName)
        if (registeredUserByDay?.horseName) horses.push(registeredUserByDay.horseName)

        if (classes && classes.length > 0) {
          classes?.forEach((currentClass) => {
            if (currentClass) {
              if (!activeClasses.includes(currentClass)) activeClasses.push(currentClass)
            }
          })
        }
      })

      if (event && uniqueRegisteredUsersByDay) {
        createEntriesMasterListPdf(
          event,
          rows,
          uniqueRegisteredUsersByDay,
          horses,
          trainers,
          activeClasses
        ).then()
      }

      toastFunctions.success({
        message: MESSAGES_CONST.REPORT_SUCCESSFULLY_DOWNLOADED,
      })
    } catch (error: any) {
      helpers.logger({
        isError: true,
        message: error,
      })
      toastFunctions.error({
        message: error?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onDownloadOrderOfGoByDayAndRing = async () => {
    try {
      const allRegistrations = await getRegisteredRiders([eventId])
      const eventBreaks = event?.breaks || []
      const feesArray = registrationFees?.registrationFees || []
      const classesByDayFacility: Record<string, { classes: string[]; facility: string }> = {}

      feesArray.forEach((fee: RegistrationFeesType) => {
        const dayKey = extractDatePart(fee.startDate)
        const facility = fee.location || 'No Facility Assigned'
        const key = `${dayKey}_${facility}`

        if (!classesByDayFacility[key]) {
          classesByDayFacility[key] = { classes: [], facility }
        }
        classesByDayFacility[key].classes.push(fee.name ?? '')
      })

      const groupedByDayFacility: Record<string, any[]> = {}

      Object.entries(classesByDayFacility).forEach(([groupKey, { classes }]) => {
        // All registrations that belong to any class on the day
        let registrationsForGroup =
          allRegistrations?.filter(
            (registration: IRegistrationByDayInterface) =>
              classes.includes(registration.registrationByDayName ?? '') &&
              (registration.paymentType !== PAYMENT_TYPE.DEFAULT ||
                registration.paymentStatus !== PAYMENT_STATUS.DEFAULT) &&
              registration.paymentStatus !== PAYMENT_STATUS.PENDING
          ) || []

        registrationsForGroup = registrationsForGroup.map(
          (registration: IRegistrationByDayInterface, index: number) => {
            const matchingFee: RegistrationFeesType | undefined = feesArray.find(
              (feeItem: RegistrationFeesType) => feeItem.name === registration.registrationByDayName
            )

            return {
              ...registration,
              riderName: registration.riderName,
              order: registration.order ?? index + 1,
              fee: matchingFee,
            }
          }
        )

        const breaksForGroup = eventBreaks.filter((brk) => brk.groupKey === groupKey) || []
        let combinedList = [...registrationsForGroup, ...breaksForGroup]
        combinedList = combinedList.sort((a, b) => (a.order || 0) - (b.order || 0))
        groupedByDayFacility[groupKey] = combinedList
      })

      // grouped data to the format needed for PDF
      const groupedData: GroupedRows[] = []

      Object.entries(groupedByDayFacility).forEach(([groupKey, registrations]) => {
        // Parse day and facility from the group key
        const [dateStr, facility] = groupKey.split('_')
        const date = parseISO(dateStr)
        const dayName = getDayOfWeek(date)

        //  rows for the PDF
        const rows: string[][] = []

        registrations.forEach((registration: any) => {
          if (registration.isBreakDrag) {
            rows.push([registration.time || '', 'Drag Break', ''])
            return
          }

          const backNumber = registration.backNumber ?? 0

          const trainer = teamMembers?.find(
            (member) => member.horseId === registration.horseId && member.memberRole === 'Trainer'
          )

          let displayTime

          if (registration.orderOfGoScratched) {
            displayTime = 'Scratched'
          } else if (scheduleBy === 'rider') {
            // rider-based scheduling, use the rideTime property with AM/PM
            if (registration.rideTime) {
              const [hours, minutes, seconds] = registration.rideTime.split(':').map(Number)

              const period = hours >= 12 ? 'PM' : 'AM'
              const hours12 = hours % 12 || 12

              const formattedHours = hours12 < 10 ? `0${hours12}` : hours12
              const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes
              const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds

              displayTime = `${formattedHours}:${formattedMinutes}:${formattedSeconds} ${period}`
            } else {
              displayTime = 'Not set'
            }
          } else if (scheduleBy === 'class') {
            //  class-based scheduling
            if (registration.fee?.increment && registration.fee.increment !== '0') {
              try {
                const fullTime = `${registration.fee.startTimeHours}:${registration.fee.startTimeMinutes} ${registration.fee.startTimeFormat}`
                displayTime = convertTime(fullTime, registration.fee.increment, 0)
              } catch (e) {
                displayTime = `${registration.fee.startTimeHours}:${registration.fee.startTimeMinutes}:00 ${registration.fee.startTimeFormat}`
              }
            } else if (registration.fee?.startTimeHours) {
              displayTime = `${registration.fee.startTimeHours}:${registration.fee.startTimeMinutes}:00 ${registration.fee.startTimeFormat}`
            } else {
              displayTime = 'N/A'
            }
          }

          rows.push([
            displayTime || 'Not set',
            `${registration.registrationByDayName || 'N/A'}<spacer>#${backNumber}`,
            `${registration.riderName} • ${registration.horseName || HORSE_NAME_DEFAULT} • ${trainer?.memberName || 'No trainer'}`,
          ])
        })

        if (rows.length > 0) {
          groupedData.push({
            day: dayName,
            date: date,
            facility: facility,
            rows: rows,
          })
        }
      })

      // Gen PDF
      if (event && groupedData.length > 0) {
        createOrderOfGoByDayAndRing(event, groupedData).then()

        toastFunctions.success({
          message: MESSAGES_CONST.REPORT_SUCCESSFULLY_DOWNLOADED,
        })
      } else {
        toastFunctions.error({
          message: 'No data available for report',
        })
      }
    } catch (error: any) {
      helpers.logger({
        isError: true,
        message: error,
      })
      toastFunctions.error({
        message: error?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onDownloadOrderOfGoByRiderPdf = async () => {
    const rows: string[][] = []

    try {
      // create rows
      const sortedUniqueRegisteredUsersByDay: IRegistrationByDayInterface[] = sortByKey(
        uniqueRegisteredUsersByDay,
        'riderName'
      )

      sortedUniqueRegisteredUsersByDay?.forEach((registeredUserByDay) => {
        const coach = teamMembers?.find(
          (member) =>
            member.horseId === registeredUserByDay.horseId && member.memberRole === 'Coach'
        )

        const coachName = coach?.memberName ?? 'No coach'

        const teamMember = teamMembers?.find(
          (member) =>
            member.horseId === registeredUserByDay.horseId && member.memberRole === 'Trainer'
        )

        const classes: { uuid: string; registrationByDayName: string }[] | null =
          registeredUsersByDay
            ?.filter(
              (registered) =>
                registered.riderId === registeredUserByDay.riderId &&
                registered.horseId === registeredUserByDay.horseId
            )
            .map((user) => ({
              uuid: user.uuid ?? '',
              registrationByDayName: user.registrationByDayName ?? '',
            })) ?? null

        classes?.forEach((currentClass, classesIndex) => {
          const registrationFee = [...registrationFees?.registrationFees]?.find(
            (fee) => fee.uuid === currentClass.uuid
          )

          // If no matching registration fee - skip iteration
          if (!registrationFee) {
            return
          }

          const fullTime = `${registrationFee.startTimeHours}:${registrationFee.startTimeMinutes} ${registrationFee.startTimeFormat}`

          const fullTimeWithSeconds = `${registrationFee.startTimeHours}:${registrationFee.startTimeMinutes}:00 ${registrationFee.startTimeFormat}`

          let displayTime
          if (registeredUserByDay?.orderOfGoScratched) {
            displayTime = 'Scratched'
          } else if (registrationFee.increment && registrationFee.increment !== '0') {
            try {
              displayTime = convertTime(fullTime, registrationFee.increment, classesIndex)
            } catch (e) {
              console.error('Error converting time: ', e)
              displayTime = fullTimeWithSeconds
            }
          } else {
            displayTime = fullTimeWithSeconds
          }

          const row = [
            `${registeredUserByDay.riderName} | ^^${coachName} • #${registeredUserByDay.backNumber ?? 0}^^`,
            `${registeredUserByDay.horseName ?? 'N/A'} | ^^${teamMember?.memberName ?? 'N/A'}^^`,
            `${currentClass.registrationByDayName}`,
            `${getDayOfWeek(registrationFee.startDate)} | ^^${convertDateString(registrationFee.startDate)}^^`,
            `${displayTime}`,
          ]

          rows.push(row)
        })
      })

      if (event && uniqueRegisteredUsersByDay) {
        createOrderOfGoByRiderPdf(event, rows).then()
      }

      toastFunctions.success({
        message: MESSAGES_CONST.REPORT_SUCCESSFULLY_DOWNLOADED,
      })
    } catch (error: any) {
      helpers.logger({
        isError: true,
        message: error,
      })
      toastFunctions.error({
        message: error?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onDownloadOrderOfGoByClassPdf = async () => {
    const rows: string[][] = []
    try {
      //  map to store rows by class name
      const classMaps: { [className: string]: { row: string[]; rideTime: string }[] } = {}

      // create rows
      const sortedUniqueRegisteredUsersByDay: IRegistrationByDayInterface[] = sortByKey(
        uniqueRegisteredUsersByDay,
        'riderName'
      )

      sortedUniqueRegisteredUsersByDay?.forEach((registeredUserByDay) => {
        const coach = teamMembers?.find(
          (member) =>
            member.horseId === registeredUserByDay.horseId && member.memberRole === 'Coach'
        )
        const coachName = coach?.memberName ?? 'No coach'
        const trainer = teamMembers?.find(
          (member) =>
            member.horseId === registeredUserByDay.horseId && member.memberRole === 'Trainer'
        )

        const backNumber = registeredUserByDay.backNumber ?? 0

        const classes =
          registeredUsersByDay
            ?.filter(
              (registered) =>
                registered.riderId === registeredUserByDay.riderId &&
                registered.horseId === registeredUserByDay.horseId
            )
            .map((user) => ({
              uuid: user.uuid ?? '',
              registrationByDayName: user.registrationByDayName ?? '',
            })) ?? null

        classes?.forEach((currentClass, classesIndex) => {
          const registrationFee = [...registrationFees?.registrationFees]?.find(
            (fee) => fee.uuid === currentClass.uuid
          )

          if (!registrationFee) {
            return
          }

          const fullTime = `${registrationFee.startTimeHours}:${registrationFee.startTimeMinutes} ${registrationFee.startTimeFormat}`
          const fullTimeWithSeconds = `${registrationFee.startTimeHours}:${registrationFee.startTimeMinutes}:00 ${registrationFee.startTimeFormat}`

          let displayTime
          if (registeredUserByDay?.orderOfGoScratched) {
            displayTime = 'Scratched'
          } else if (
            registrationFee.increment &&
            registrationFee.increment !== '0' &&
            isValidIncrementFormat(registrationFee.increment)
          ) {
            try {
              displayTime = convertTime(fullTime, registrationFee.increment, classesIndex)
            } catch (e) {
              console.error('Error converting time: ', e)
              displayTime = fullTimeWithSeconds
            }
          } else {
            displayTime = fullTimeWithSeconds
          }

          const row = [
            `${registeredUserByDay.riderName} | ^^${coachName}^^`,
            `${registeredUserByDay.horseName ?? HORSE_NAME_DEFAULT} | ^^${trainer?.memberName ?? 'No trainer'}^^`,
            `${currentClass.registrationByDayName || 'N/A'}<spacer>#${backNumber}`,
            `${displayTime}`,
          ]

          let sortableTime
          if (displayTime === 'Scratched') {
            sortableTime = '99:99:99' //  scratched entries at the end
          } else {
            const timeMatch = displayTime.match(/(\d+):(\d+)(?::(\d+))?\s*(AM|PM)/i)
            if (timeMatch) {
              let [_, hours, minutes, seconds, ampm] = timeMatch
              seconds = seconds || '00'

              // convert to 24-hour format for sort
              let hour24 = parseInt(hours)
              if (ampm.toUpperCase() === 'PM' && hour24 < 12) hour24 += 12
              if (ampm.toUpperCase() === 'AM' && hour24 === 12) hour24 = 0

              // sortable str
              sortableTime = `${hour24.toString().padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`
            } else {
              sortableTime = displayTime
            }
          }

          // row with time in the class map
          if (!classMaps[currentClass.registrationByDayName]) {
            classMaps[currentClass.registrationByDayName] = []
          }

          classMaps[currentClass.registrationByDayName].push({
            row: row,
            rideTime: sortableTime,
          })
        })
      })

      // sort each class group by ride time - flatten into rows array
      Object.entries(classMaps).forEach(([className, rowsWithTime]) => {
        rowsWithTime.sort((a, b) => a.rideTime.localeCompare(b.rideTime))

        rowsWithTime.forEach((item) => rows.push(item.row))
      })

      if (event && uniqueRegisteredUsersByDay) {
        createOrderOfGoByClassPdf(event, groupByKey(rows, 2)).then()
      }

      toastFunctions.success({
        message: MESSAGES_CONST.REPORT_SUCCESSFULLY_DOWNLOADED,
      })
    } catch (error: any) {
      helpers.logger({
        isError: true,
        message: error,
      })
      toastFunctions.error({
        message: error?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onDownloadClassScheduleByDayAndFacilityPdf = async () => {
    try {
      const feesArray = registrationFees?.registrationFees || []
      const classesByDayFacility: Record<
        string,
        { classes: RegistrationFeesType[]; facility: string }
      > = {}

      const flattenedFeesArray = feesArray.flatMap((item: RegistrationFeesType) => {
        if (item?.children?.[0]) return [...item.children]
        return [item]
      })

      const allScheduleItems = [...flattenedFeesArray, ...(customScheduling || [])]

      // Group classes by day and facility
      allScheduleItems.forEach((fee: RegistrationFeesType) => {
        const dayKey = extractDatePart(fee.startDate)
        const facility = fee.location || 'No Facility Assigned'
        const key = `${dayKey}_${facility}`

        if (!classesByDayFacility[key]) {
          classesByDayFacility[key] = { classes: [], facility }
        }

        classesByDayFacility[key].classes.push(fee)
      })

      //  grouped data to the format needed for PDF
      const groupedData: GroupedRows[] = []

      Object.entries(classesByDayFacility).forEach(([groupKey, { classes, facility }]) => {
        // parse day and facility from the group key
        const [dateStr, facilityName] = groupKey.split('_')
        const date = parseISO(dateStr)
        const dayName = getDayOfWeek(date)

        // sort classes by start time
        const sortedClasses = classes.sort((a, b) => {
          const timeA = `${a.startTimeHours}:${a.startTimeMinutes} ${a.startTimeFormat}`
          const timeB = `${b.startTimeHours}:${b.startTimeMinutes} ${b.startTimeFormat}`
          return timeA.localeCompare(timeB)
        })

        // rows for the PDF
        const rows: string[][] = sortedClasses.map((classFee) => {
          // Format time
          const displayTime = `${classFee.startTimeHours}:${classFee.startTimeMinutes} ${classFee.startTimeFormat}`

          return [`${displayTime} | ^^${dayName}^^`, facilityName, classFee.name ?? 'N/A']
        })

        if (rows.length > 0) {
          groupedData.push({
            day: dayName,
            date: date,
            facility: facilityName,
            rows: rows,
          })
        }
      })

      // grouped data by date
      groupedData.sort((a, b) => a.date.getTime() - b.date.getTime())

      // Generate PDF
      if (event && groupedData.length > 0) {
        createClassScheduleByDayAndFacility(event, groupedData).then()

        toastFunctions.success({
          message: MESSAGES_CONST.REPORT_SUCCESSFULLY_DOWNLOADED,
        })
      } else {
        toastFunctions.error({
          message: 'No data available for report',
        })
      }
    } catch (error: any) {
      helpers.logger({
        isError: true,
        message: error,
      })
      toastFunctions.error({
        message: error?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  return (
    <ReportsTabWrapper
      title="Event Reports"
      description="Export and print event reports"
      activeOption={activeOption}
      setActiveOption={setActiveOption}
    >
      <div className="flex flex-wrap gap-x-[2%] gap-y-5">
        <EventReportItem
          isAvailable
          title="Entries"
          description="master list"
          onClick={onDownloadEntriesPdf}
        />
        {scheduleBy === 'class' ? (
          <EventReportItem
            isAvailable
            title="Order of Go"
            description="by class"
            onClick={onDownloadOrderOfGoByClassPdf}
          />
        ) : (
          <EventReportItem
            isAvailable
            title="Order of Go"
            description="by day and ring"
            onClick={onDownloadOrderOfGoByDayAndRing}
          />
        )}
        <EventReportItem
          isAvailable
          title="Order of Go"
          description="by rider"
          onClick={onDownloadOrderOfGoByRiderPdf}
        />
        <EventReportItem title="Show Schedule" description="by day" onClick={async () => {}} />
        <EventReportItem
          isAvailable
          title="Class schedule"
          description="by day and facility"
          onClick={onDownloadClassScheduleByDayAndFacilityPdf}
        />
      </div>
    </ReportsTabWrapper>
  )
}

export default EventReportsReport
